import * as THREE from 'three';
import {
  addBeam,
  addMesh,
  createGableGeometry,
  createPennantGeometry,
  finishModel,
  lowPolyMaterial,
  type VectorTuple,
} from './modelHelpers';

const snow = lowPolyMaterial('lanternwood-snow', 0xf4faf7, {
  emissive: 0xbad6ce,
  emissiveIntensity: 0.08,
});
const darkWood = lowPolyMaterial('dark-hewn-wood', 0x513f39);
const warmWood = lowPolyMaterial('warm-hewn-wood', 0x8d6750);
const cutWood = lowPolyMaterial('fresh-cut-wood', 0xc59b70);
const charcoal = lowPolyMaterial('charcoal-detail', 0x28373a);
const amber = lowPolyMaterial('welcoming-window-light', 0xffc25c, {
  emissive: 0xff8b2c,
  emissiveIntensity: 1.25,
  roughness: 0.62,
});
const pine = lowPolyMaterial('pine-needle-green', 0x3f765e);
const birch = lowPolyMaterial('birch-bark', 0xebe9dc);
const twig = lowPolyMaterial('winter-twigs', 0x57483f);
const carrot = lowPolyMaterial('carrot-orange', 0xe97733);
const coral = lowPolyMaterial('soft-coral-cloth', 0xc94f55);
const hareFur = lowPolyMaterial('hare-winter-fur', 0xe8e3d7);
const hareShadow = lowPolyMaterial('hare-shadow-fur', 0xb4ada1);
const blush = lowPolyMaterial('soft-ear-pink', 0xd8908f, { side: THREE.DoubleSide });
const foxRusset = lowPolyMaterial('fox-winter-russet', 0xc45f32, {
  emissive: 0x672716,
  emissiveIntensity: 0.09,
});
const foxDark = lowPolyMaterial('fox-dark-russet', 0x743c2d);
const foxCream = lowPolyMaterial('fox-winter-cream', 0xeee5d5);
const trackShadow = lowPolyMaterial('powder-track-shadow', 0x88aaa6, {
  roughness: 1,
});

function addWindow(
  group: THREE.Group,
  name: string,
  position: VectorTuple,
  rotation: VectorTuple = [0, 0, 0],
): void {
  addMesh(group, `${name}-frame`, new THREE.BoxGeometry(1.62, 1.42, 0.15), darkWood, {
    position,
    rotation,
  });
  const panePosition: VectorTuple = rotation[1] === 0
    ? [position[0], position[1], position[2] + 0.085]
    : [position[0] + Math.sign(rotation[1]) * 0.085, position[1], position[2]];
  addMesh(group, `${name}-glow`, new THREE.BoxGeometry(1.32, 1.12, 0.09), amber, {
    position: panePosition,
    rotation,
  });
  addBeam(group, `${name}-vertical-mullion`,
    [panePosition[0], panePosition[1] - 0.58, panePosition[2] + 0.06],
    [panePosition[0], panePosition[1] + 0.58, panePosition[2] + 0.06],
    0.055,
    darkWood,
    0.055,
    4,
  );
}

export function createRangerHutModel(): THREE.Group {
  const group = new THREE.Group();
  const roofAngle = Math.atan2(2.05, 3.82);
  const roofLength = Math.hypot(2.05, 3.82);

  addMesh(group, 'hut-log-body', new THREE.BoxGeometry(7, 3.45, 5.1), warmWood, {
    position: [0, 1.72, 0],
  });
  addMesh(group, 'front-gable', createGableGeometry(7, 2.05, 5.1), warmWood, {
    position: [0, 3.44, 0],
  });

  for (const x of [-3.38, 3.38]) {
    for (const z of [-2.42, 2.42]) {
      addBeam(group, `corner-log-${x}-${z}`, [x, 0.08, z], [x, 3.55, z], 0.17, darkWood, 0.17, 6);
    }
  }
  for (const y of [0.16, 1.04, 1.92, 2.8, 3.4]) {
    addBeam(group, `front-log-${y}`, [-3.42, y, 2.52], [3.42, y, 2.52], 0.12, darkWood, 0.12, 6);
  }

  for (const side of [-1, 1]) {
    const x = side * 1.9;
    const rotationZ = side * roofAngle;
    addMesh(group, `roof-${side}`, new THREE.BoxGeometry(roofLength, 0.28, 6.05), darkWood, {
      position: [x, 4.48, 0],
      rotation: [0, 0, rotationZ],
    });
    addMesh(group, `deep-snow-roof-${side}`, new THREE.BoxGeometry(roofLength + 0.2, 0.32, 6.24), snow, {
      position: [x, 4.69, 0],
      rotation: [0, 0, rotationZ],
    });
  }

  addMesh(group, 'front-door', new THREE.BoxGeometry(1.55, 2.55, 0.18), darkWood, {
    position: [-1.7, 1.3, 2.61],
  });
  addMesh(group, 'door-inset', new THREE.BoxGeometry(1.24, 2.25, 0.11), warmWood, {
    position: [-1.7, 1.3, 2.72],
  });
  addMesh(group, 'door-window', new THREE.BoxGeometry(0.58, 0.52, 0.08), amber, {
    position: [-1.7, 1.82, 2.8],
  });
  addMesh(group, 'door-handle', new THREE.DodecahedronGeometry(0.09, 0), charcoal, {
    position: [-1.18, 1.18, 2.87],
  });
  addWindow(group, 'front-window', [1.48, 1.88, 2.61]);
  addWindow(group, 'side-window', [-3.59, 1.82, -0.42], [0, -Math.PI / 2, 0]);

  addMesh(group, 'stone-chimney', new THREE.CylinderGeometry(0.42, 0.52, 2.3, 6), charcoal, {
    position: [2.02, 5.2, -0.82],
  });
  addMesh(group, 'chimney-snow-cap', new THREE.CylinderGeometry(0.58, 0.5, 0.18, 6), snow, {
    position: [2.02, 6.4, -0.82],
  });

  addMesh(group, 'porch-step', new THREE.BoxGeometry(2.2, 0.32, 1.05), darkWood, {
    position: [-1.55, 0.16, 3.05],
  });
  addMesh(group, 'porch-snow', new THREE.BoxGeometry(2.08, 0.13, 0.94), snow, {
    position: [-1.55, 0.39, 3.05],
  });
  for (const x of [-2.8, -2.35, -1.9]) {
    addBeam(group, `firewood-${x}`, [x, 0.32, -2.82], [x, 1.48, -2.82], 0.18, darkWood, 0.16, 6);
  }

  return finishModel('ranger-hut', 'Lanternwood Ranger Hut', 'lanternwood', group);
}

interface BirchStem {
  readonly base: VectorTuple;
  readonly bend: VectorTuple;
  readonly top: VectorTuple;
  readonly radius: number;
}

export function createBirchClusterModel(): THREE.Group {
  const group = new THREE.Group();
  const stems: readonly BirchStem[] = [
    { base: [-1.38, 0, 0.36], bend: [-1.62, 3.65, 0.18], top: [-2.18, 7.05, -0.28], radius: 0.34 },
    { base: [-0.42, 0, -0.48], bend: [-0.18, 4.24, -0.67], top: [0.52, 8.65, -1.02], radius: 0.27 },
    { base: [0.52, 0, 0.5], bend: [1.02, 3.92, 0.74], top: [1.78, 7.62, 1.08], radius: 0.3 },
    { base: [1.32, 0, -0.3], bend: [1.1, 3.18, -0.47], top: [0.48, 6.3, -0.82], radius: 0.2 },
  ];

  stems.forEach((stem, index) => {
    addBeam(group, `birch-lower-trunk-${index}`, stem.base, stem.bend, stem.radius, birch, stem.radius * 0.78, 7);
    addBeam(group, `birch-upper-trunk-${index}`, stem.bend, stem.top, stem.radius * 0.8, birch, stem.radius * 0.47, 7);

    const start = new THREE.Vector3(...stem.base);
    const bend = new THREE.Vector3(...stem.bend);
    const end = new THREE.Vector3(...stem.top);
    const scarLevels = [0.2, 0.43, 0.7, 1.2, 1.48];
    scarLevels.forEach((level, scarIndex) => {
      const onUpperStem = level > 1;
      const amount = onUpperStem ? level - 1 : level;
      const scarPosition = (onUpperStem ? bend : start).clone().lerp(onUpperStem ? end : bend, amount);
      const facing = (index * 1.35 + scarIndex * 1.9) % (Math.PI * 2);
      const localRadius = stem.radius * (onUpperStem ? 0.67 : 0.9);
      addMesh(group, `birch-bark-scar-${index}-${scarIndex}`, new THREE.BoxGeometry(
        stem.radius * (scarIndex % 2 === 0 ? 1.8 : 1.28),
        scarIndex % 3 === 0 ? 0.17 : 0.1,
        0.045,
      ), charcoal, {
        position: [
          scarPosition.x + Math.sin(facing) * localRadius,
          scarPosition.y,
          scarPosition.z + Math.cos(facing) * localRadius,
        ],
        rotation: [0, facing, scarIndex % 2 === 0 ? 0.08 : -0.06],
      });
    });

    [0.24, 0.62].forEach((amount, branchIndex) => {
      const shoulder = bend.clone().lerp(end, amount);
      const branchSide = (index + branchIndex) % 2 === 0 ? -1 : 1;
      const branchEnd: VectorTuple = [
        shoulder.x + branchSide * (0.82 + index * 0.08 + branchIndex * 0.2),
        shoulder.y + 0.62 + branchIndex * 0.18,
        shoulder.z + (branchIndex === 0 ? 0.54 : -0.42),
      ];
      addBeam(
        group,
        `birch-branch-${index}-${branchIndex}`,
        [shoulder.x, shoulder.y, shoulder.z],
        branchEnd,
        stem.radius * 0.38,
        twig,
        stem.radius * 0.16,
        5,
      );
      const forkA: VectorTuple = [
        branchEnd[0] + branchSide * (0.44 + branchIndex * 0.1),
        branchEnd[1] + 0.68,
        branchEnd[2] + 0.2,
      ];
      const forkB: VectorTuple = [
        branchEnd[0] - branchSide * 0.14,
        branchEnd[1] + 0.55,
        branchEnd[2] - 0.42,
      ];
      addBeam(group, `birch-twig-${index}-${branchIndex}-a`, branchEnd, forkA, 0.06, twig, 0.025, 5);
      addBeam(group, `birch-twig-${index}-${branchIndex}-b`, branchEnd, forkB, 0.052, twig, 0.02, 5);

      if ((index + branchIndex) % 3 !== 1) {
        addMesh(group, `branch-snow-${index}-${branchIndex}`, new THREE.IcosahedronGeometry(0.5, 1), snow, {
          position: [branchEnd[0] - branchSide * 0.04, branchEnd[1] + 0.12, branchEnd[2]],
          scale: [1.05 + branchIndex * 0.35, 0.23 + index * 0.025, 0.48 + index * 0.04],
          rotation: [0, branchSide * (0.18 + index * 0.08), branchSide * 0.08],
        });
      }
    });
  });

  addMesh(group, 'birch-base-drift', new THREE.IcosahedronGeometry(1, 1), snow, {
    position: [-0.05, 0.11, 0],
    scale: [2.12, 0.28, 1.26],
  });
  return finishModel('birch-cluster', 'Silver Birch Cluster', 'lanternwood', group);
}

export function createFallenLogModel(): THREE.Group {
  const group = new THREE.Group();
  addBeam(group, 'fallen-trunk-left', [-3.3, 0.62, -0.2], [-1.15, 0.78, -0.05], 0.74, darkWood, 0.67, 9);
  addBeam(group, 'fallen-trunk-middle', [-1.18, 0.78, -0.05], [1.3, 0.86, 0.16], 0.69, darkWood, 0.61, 9);
  addBeam(group, 'fallen-trunk-right', [1.27, 0.86, 0.16], [3.28, 0.64, 0.43], 0.62, darkWood, 0.47, 8);
  addMesh(group, 'ragged-broken-end', new THREE.DodecahedronGeometry(0.7, 0), darkWood, {
    position: [-3.3, 0.62, -0.2],
    scale: [0.5, 1.02, 0.9],
    rotation: [0.2, 0.08, -0.12],
  });
  addMesh(group, 'cut-end', new THREE.CircleGeometry(0.46, 8), cutWood, {
    position: [3.3, 0.64, 0.435],
    rotation: [0, Math.PI / 2 + 0.13, 0.1],
  });
  addMesh(group, 'cut-end-ring', new THREE.TorusGeometry(0.31, 0.034, 4, 8), darkWood, {
    position: [3.315, 0.64, 0.438],
    rotation: [0, Math.PI / 2 + 0.13, 0.1],
  });
  addBeam(group, 'bark-ridge-front', [-2.68, 0.94, 0.42], [0.55, 1.11, 0.67], 0.075, warmWood, 0.045, 5);
  addBeam(group, 'bark-ridge-rear', [-0.78, 1.02, -0.58], [2.58, 0.86, -0.22], 0.065, warmWood, 0.035, 5);
  addBeam(group, 'broken-branch-left', [-0.62, 1.03, -0.25], [-0.96, 1.78, -0.72], 0.16, darkWood, 0.055, 6);
  addBeam(group, 'broken-branch-right', [1.55, 0.98, 0.25], [1.87, 1.5, 0.78], 0.13, darkWood, 0.045, 6);

  const snowPatches = [
    { position: [-2.3, 1.18, -0.04] as VectorTuple, scale: [1.55, 0.31, 0.82] as VectorTuple, rotation: [0.02, -0.12, 0.04] as VectorTuple },
    { position: [-0.55, 1.29, 0.03] as VectorTuple, scale: [1.72, 0.4, 0.92] as VectorTuple, rotation: [-0.02, 0.1, -0.025] as VectorTuple },
    { position: [1.25, 1.28, 0.17] as VectorTuple, scale: [1.4, 0.3, 0.77] as VectorTuple, rotation: [0.04, -0.14, -0.06] as VectorTuple },
    { position: [2.45, 1.08, 0.32] as VectorTuple, scale: [0.82, 0.24, 0.62] as VectorTuple, rotation: [0, 0.2, -0.11] as VectorTuple },
  ];
  snowPatches.forEach((patch, index) => {
    addMesh(group, `log-snow-patch-${index}`, new THREE.IcosahedronGeometry(0.78, 1), snow, patch);
  });

  addBeam(group, 'nurse-sapling-trunk', [-1.78, 1.08, -0.1], [-1.58, 3.7, -0.14], 0.095, twig, 0.038, 6);
  const saplingTiers = [
    { y: 1.82, radius: 0.76, height: 0.98 },
    { y: 2.3, radius: 0.66, height: 0.9 },
    { y: 2.75, radius: 0.54, height: 0.8 },
    { y: 3.15, radius: 0.41, height: 0.68 },
  ];
  saplingTiers.forEach((tier, index) => {
    addMesh(group, `nurse-sapling-tier-${index}`, new THREE.ConeGeometry(tier.radius, tier.height, 7), pine, {
      position: [-1.73 + index * 0.05, tier.y, -0.12],
      rotation: [0, index * 0.42, -0.045],
    });
  });
  return finishModel('fallen-log', 'Snow-Covered Nurse Log', 'lanternwood', group);
}

export function createSnowmanModel(): THREE.Group {
  const group = new THREE.Group();
  addMesh(group, 'snowman-base', new THREE.IcosahedronGeometry(1.15, 1), snow, {
    position: [0, 1.08, 0],
    scale: [1.08, 1, 1.02],
  });
  addMesh(group, 'snowman-middle', new THREE.IcosahedronGeometry(0.86, 1), snow, {
    position: [0.04, 2.55, 0],
  });
  addMesh(group, 'snowman-head', new THREE.IcosahedronGeometry(0.66, 1), snow, {
    position: [-0.03, 3.68, 0.02],
  });
  for (const x of [-0.23, 0.22]) {
    addMesh(group, `coal-eye-${x}`, new THREE.DodecahedronGeometry(0.075, 0), charcoal, {
      position: [x, 3.82, 0.61],
    });
  }
  addMesh(group, 'carrot-nose', new THREE.ConeGeometry(0.12, 0.72, 6), carrot, {
    position: [-0.02, 3.63, 0.92],
    rotation: [Math.PI / 2, 0, 0],
  });
  for (const [index, x] of [-0.26, 0, 0.26].entries()) {
    addMesh(group, `coal-smile-${index}`, new THREE.DodecahedronGeometry(0.055, 0), charcoal, {
      position: [x, 3.43 + Math.abs(x) * 0.2, 0.62],
    });
  }
  for (const [index, y] of [2.28, 2.62, 2.94].entries()) {
    addMesh(group, `coal-button-${index}`, new THREE.DodecahedronGeometry(0.08, 0), charcoal, {
      position: [0.02, y, 0.82],
    });
  }
  addMesh(group, 'scarf-collar', new THREE.TorusGeometry(0.65, 0.13, 5, 10), coral, {
    position: [0, 3.15, 0],
    rotation: [Math.PI / 2, 0, 0],
  });
  addMesh(group, 'scarf-tail', createPennantGeometry(1.35, 0.56), coral, {
    position: [0.36, 3.18, 0.25],
    rotation: [0, -0.2, -0.35],
  });
  addBeam(group, 'left-twig-arm', [-0.68, 2.74, 0], [-1.76, 3.42, 0.06], 0.07, twig, 0.035, 5);
  addBeam(group, 'right-twig-arm', [0.7, 2.7, 0], [1.68, 3.15, -0.1], 0.07, twig, 0.035, 5);
  addBeam(group, 'right-twig-finger', [1.43, 3.04, -0.06], [1.74, 3.5, -0.08], 0.032, twig, 0.015, 5);
  addMesh(group, 'wool-hat', new THREE.ConeGeometry(0.58, 0.9, 7), coral, {
    position: [-0.07, 4.46, -0.02],
    rotation: [0, 0, -0.12],
  });
  addMesh(group, 'hat-pom', new THREE.DodecahedronGeometry(0.17, 0), coral, {
    position: [-0.18, 4.93, -0.02],
  });
  return finishModel('snowman', 'Scarfed Trail Snowman', 'lanternwood', group);
}

export function createSnowshoeHareModel(): THREE.Group {
  const group = new THREE.Group();
  addMesh(group, 'hare-body', new THREE.IcosahedronGeometry(0.72, 1), hareFur, {
    position: [0, 0.78, 0],
    scale: [1.26, 0.82, 0.78],
    rotation: [0.04, 0, -0.06],
  });
  addMesh(group, 'hare-chest', new THREE.IcosahedronGeometry(0.48, 1), hareShadow, {
    position: [0.67, 1.05, 0],
    scale: [0.72, 1.15, 0.84],
  });
  addMesh(group, 'hare-head', new THREE.IcosahedronGeometry(0.43, 1), hareFur, {
    position: [0.88, 1.58, 0],
    scale: [0.92, 1, 0.88],
  });
  addMesh(group, 'hare-muzzle', new THREE.IcosahedronGeometry(0.2, 0), hareFur, {
    position: [1.22, 1.47, 0],
    scale: [1.05, 0.72, 0.95],
  });
  for (const [index, z] of [-0.2, 0.2].entries()) {
    addMesh(group, `hare-ear-${index}`, new THREE.ConeGeometry(0.23, 1.34, 6), hareFur, {
      position: [0.76 - index * 0.11, 2.37, z],
      rotation: [0, 0, index === 0 ? -0.18 : -0.08],
      scale: [0.82, 1, 0.65],
    });
    addMesh(group, `hare-inner-ear-${index}`, createPennantGeometry(0.83, 0.2), blush, {
      position: [0.83 - index * 0.1, 2.71, z + (index === 0 ? -0.15 : 0.15)],
      rotation: [0, -Math.PI / 2, index === 0 ? -1.7 : -1.6],
    });
  }
  for (const z of [-0.3, 0.3]) {
    addMesh(group, `hare-eye-${z}`, new THREE.DodecahedronGeometry(0.065, 0), charcoal, {
      position: [1.14, 1.68, z],
    });
    addMesh(group, `hare-hind-paw-${z}`, new THREE.IcosahedronGeometry(0.31, 0), hareShadow, {
      position: [-0.55, 0.29, z],
      scale: [1.65, 0.42, 0.67],
    });
    addMesh(group, `hare-front-paw-${z}`, new THREE.IcosahedronGeometry(0.22, 0), hareFur, {
      position: [0.71, 0.28, z * 0.78],
      scale: [1.15, 0.45, 0.62],
    });
  }
  addMesh(group, 'hare-tail', new THREE.DodecahedronGeometry(0.33, 0), snow, {
    position: [-0.87, 0.9, 0],
  });
  addMesh(group, 'hare-nose', new THREE.DodecahedronGeometry(0.065, 0), charcoal, {
    position: [1.41, 1.51, 0],
  });
  return finishModel('snowshoe-hare', 'Listening Snowshoe Hare', 'lanternwood', group);
}

function createDiveCraterGeometry(): THREE.BufferGeometry {
  const segments = 11;
  const positions: number[] = [];
  const indices: number[] = [];
  for (let index = 0; index < segments; index += 1) {
    const angle = index / segments * Math.PI * 2;
    const outerRadius = 0.94 + (index % 3) * 0.06;
    const innerRadius = 0.48 + ((index * 2) % 3) * 0.035;
    positions.push(
      Math.cos(angle) * outerRadius,
      0,
      Math.sin(angle) * outerRadius * 0.83,
      Math.cos(angle) * innerRadius,
      0.18 + (index % 4) * 0.025,
      Math.sin(angle) * innerRadius * 0.8,
    );
  }
  for (let index = 0; index < segments; index += 1) {
    const next = (index + 1) % segments;
    const outer = index * 2;
    const inner = outer + 1;
    const outerNext = next * 2;
    const innerNext = outerNext + 1;
    indices.push(outer, inner, outerNext, outerNext, inner, innerNext);
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

interface FoxTailStation {
  readonly position: VectorTuple;
  readonly radius: number;
}

/** A continuous faceted plume with no capped joints between authored bends. */
function createFoxTailGeometry(stations: readonly FoxTailStation[]): THREE.BufferGeometry {
  const sides = 7;
  const positions: number[] = [];
  const indices: number[] = [];

  stations.forEach((station, stationIndex) => {
    const previous = new THREE.Vector3(...stations[Math.max(0, stationIndex - 1)]!.position);
    const next = new THREE.Vector3(...stations[Math.min(stations.length - 1, stationIndex + 1)]!.position);
    const tangent = next.sub(previous).normalize();
    const across = new THREE.Vector3(0, 0, 1);
    const upright = across.clone().cross(tangent).normalize();
    const center = new THREE.Vector3(...station.position);
    for (let side = 0; side < sides; side += 1) {
      const angle = side / sides * Math.PI * 2 + stationIndex * 0.11;
      const point = center.clone()
        .addScaledVector(upright, Math.cos(angle) * station.radius)
        .addScaledVector(across, Math.sin(angle) * station.radius);
      positions.push(point.x, point.y, point.z);
    }
  });

  for (let station = 0; station < stations.length - 1; station += 1) {
    for (let side = 0; side < sides; side += 1) {
      const nextSide = (side + 1) % sides;
      const start = station * sides + side;
      const startNext = station * sides + nextSide;
      const end = (station + 1) * sides + side;
      const endNext = (station + 1) * sides + nextSide;
      indices.push(start, end, startNext, startNext, end, endNext);
    }
  }

  const startCenter = positions.length / 3;
  positions.push(...stations[0]!.position);
  const endCenter = positions.length / 3;
  positions.push(...stations[stations.length - 1]!.position);
  for (let side = 0; side < sides; side += 1) {
    const nextSide = (side + 1) % sides;
    indices.push(startCenter, nextSide, side);
    const lastRing = (stations.length - 1) * sides;
    indices.push(endCenter, lastRing + side, lastRing + nextSide);
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

/** A fox caught at the instant its mousing pounce disappears into the snow. */
export function createSnowDivingFoxModel(): THREE.Group {
  const group = new THREE.Group();

  // The rising tail, haunches, and descending body make one uninterrupted
  // action line. One indexed surface replaces the earlier row of capped
  // cylinders, while the cream tip keeps enough volume to feel winter-plush.
  addMesh(group, 'diving-fox-tail-plume', createFoxTailGeometry([
    { position: [-0.88, 2.28, 0.04], radius: 0.42 },
    { position: [-1.45, 2.87, 0.08], radius: 0.5 },
    { position: [-2.18, 3.08, 0.04], radius: 0.46 },
    { position: [-2.82, 2.78, -0.02], radius: 0.34 },
    { position: [-3.14, 2.5, -0.06], radius: 0.28 },
  ]), foxRusset);
  addMesh(group, 'diving-fox-tail-cream-tip', createFoxTailGeometry([
    { position: [-3.14, 2.5, -0.06], radius: 0.285 },
    { position: [-3.43, 2.17, -0.08], radius: 0.22 },
    { position: [-3.6, 1.92, -0.09], radius: 0.085 },
  ]), foxCream);

  addMesh(group, 'diving-fox-haunches', new THREE.IcosahedronGeometry(0.78, 1), foxRusset, {
    position: [-0.52, 2.15, 0],
    scale: [1.02, 0.94, 0.88],
    rotation: [0.02, 0, -0.36],
  });
  addMesh(group, 'diving-fox-body', new THREE.IcosahedronGeometry(1, 1), foxRusset, {
    position: [0.05, 1.64, 0],
    scale: [1.22, 0.66, 0.61],
    rotation: [0, 0, -0.59],
  });
  addMesh(group, 'diving-fox-chest', new THREE.IcosahedronGeometry(0.55, 0), foxCream, {
    position: [0.66, 1.05, 0],
    scale: [0.82, 1.04, 0.92],
    rotation: [0, 0, -0.52],
  });
  addMesh(group, 'diving-fox-head', new THREE.IcosahedronGeometry(0.52, 1), foxRusset, {
    position: [0.92, 0.72, 0],
    scale: [1.04, 0.9, 0.86],
    rotation: [0, 0, -0.57],
  });
  addMesh(group, 'diving-fox-muzzle', new THREE.IcosahedronGeometry(0.28, 0), foxCream, {
    position: [1.28, 0.39, 0],
    scale: [1.25, 0.76, 0.86],
    rotation: [0, 0, -0.43],
  });
  addMesh(group, 'diving-fox-nose', new THREE.DodecahedronGeometry(0.09, 0), charcoal, {
    position: [1.52, 0.25, 0],
  });

  for (const [index, z] of [-0.3, 0.3].entries()) {
    addMesh(group, `diving-fox-ear-${index}`, new THREE.ConeGeometry(0.22, 0.62, 6), foxDark, {
      position: [0.7 - index * 0.05, 1.13, z],
      rotation: [0, 0, index === 0 ? -0.42 : -0.32],
      scale: [0.86, 1, 0.7],
    });
    addMesh(group, `diving-fox-eye-${index}`, new THREE.DodecahedronGeometry(0.052, 0), charcoal, {
      position: [1.13, 0.8, z * 1.22],
    });

    // Forelegs point into the same hollow as the muzzle and vanish behind its
    // lip; this makes the contact with the snow intentional from both angles.
    addBeam(
      group,
      `diving-fox-foreleg-${index}`,
      [0.48, 1.2, z * 0.88],
      [1.06, 0.32, z],
      0.14,
      foxDark,
      0.095,
      6,
    );
    addMesh(group, `diving-fox-buried-paw-${index}`, new THREE.IcosahedronGeometry(0.18, 0), foxDark, {
      position: [1.14, 0.24, z],
      scale: [1.3, 0.5, 0.72],
      rotation: [0, 0, -0.48],
    });

    // Tucked hind feet remain conspicuously airborne, selling the split
    // second of the leap even when the fox is viewed from behind.
    addBeam(
      group,
      `diving-fox-hind-leg-${index}`,
      [-0.58, 2.1, z * 1.04],
      [-1.08, 1.55, z * 1.2],
      0.19,
      foxDark,
      0.12,
      6,
    );
    addMesh(group, `diving-fox-airborne-paw-${index}`, new THREE.IcosahedronGeometry(0.23, 0), foxDark, {
      position: [-1.3, 1.45, z * 1.2],
      scale: [1.45, 0.52, 0.66],
      rotation: [0, index === 0 ? -0.12 : 0.12, 0.28],
    });
  }

  addMesh(group, 'diving-fox-hollow', new THREE.CircleGeometry(0.52, 11), trackShadow, {
    position: [1.22, 0.012, 0],
    rotation: [-Math.PI / 2, 0, 0],
    scale: [1.1, 0.82, 1],
  });
  addMesh(group, 'diving-fox-crater-lip', createDiveCraterGeometry(), snow, {
    position: [1.22, 0, 0],
    rotation: [0, 0.08, 0],
    scale: [1.12, 1, 1.04],
  });

  const powderBursts = [
    { position: [1.45, 0.48, -0.66] as VectorTuple, scale: [0.34, 0.2, 0.26] as VectorTuple },
    { position: [1.76, 0.35, -0.22] as VectorTuple, scale: [0.28, 0.16, 0.22] as VectorTuple },
    { position: [1.64, 0.42, 0.53] as VectorTuple, scale: [0.32, 0.18, 0.25] as VectorTuple },
    { position: [0.94, 0.48, 0.72] as VectorTuple, scale: [0.25, 0.16, 0.2] as VectorTuple },
  ] as const;
  powderBursts.forEach((burst, index) => {
    addMesh(group, `diving-fox-powder-burst-${index}`, new THREE.OctahedronGeometry(1, 0), snow, {
      position: burst.position,
      scale: burst.scale,
      rotation: [index * 0.14, index * 0.47, index % 2 ? 0.2 : -0.16],
    });
  });

  // Alternating impressions behind the pounce are compact enough to stay
  // inside the tail's footprint, so the gallery framing is not inflated.
  const prints = [
    { x: -2.72, z: 0.72, rotation: -0.2 },
    { x: -2.08, z: -0.72, rotation: 0.16 },
    { x: -1.48, z: 0.74, rotation: -0.12 },
  ] as const;
  const trackGeometry = new THREE.DodecahedronGeometry(0.16, 0);
  const toeGeometry = new THREE.OctahedronGeometry(0.065, 0);
  prints.forEach((print, index) => {
    addMesh(group, `diving-fox-track-pad-${index}`, trackGeometry, trackShadow, {
      position: [print.x, 0.025, print.z],
      scale: [1.18, 0.14, 0.78],
      rotation: [0, print.rotation, 0],
    });
    for (const [toeIndex, toeZ] of [-0.07, 0.07].entries()) {
      addMesh(group, `diving-fox-track-toe-${index}-${toeIndex}`, toeGeometry, trackShadow, {
        position: [print.x + 0.18, 0.022, print.z + toeZ],
        scale: [0.8, 0.14, 0.7],
        rotation: [0, print.rotation, 0],
      });
    }
  });

  return finishModel('snow-diving-fox', 'Powder-Diving Red Fox', 'lanternwood', group);
}
